import { track } from './tracking/track';
import addStartupScript from './events/addStartupScript';
import noSearchResults from '../templates/course_list/course_list_no_results.mustache';
import CourseData from './course_list/course_data';
import CourseListFilters from './course_list/course_list_filters';
import CourseListPagination from './course_list/course_list_pagination';
import CourseListSearch from './course_list/course_list_search';
import CourseSearch from './course_search';
import { handleEvent } from './tracking/events';
import hookUI from '../templates/hook.mustache';
import queryString from 'query-string';
import scrollTo from './animation/scrollTo';
import mappedClientFilters from './course_list/mapped_client_filters';
import Marked from 'marked';
import logError from './util/logError';

const COURSES_SELECTOR = '.js-course-list__courses';
const FILTER_WRAPPER_SELECTOR = '.js-course-list-filters-wrapper';
const PAGINATION_SELECTOR = '.course-list__pagination';
const SEARCH_SELECTOR = '.course-list__search';
const WRAPPER_SELECTOR = '.course-list';
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Use js-events to track search activity
 * trackSearch.
 *
  * @param {object} searchParams Details of search
 */
const trackSearch = (searchParams) => {
  const target = 'course_list';
  const category = 'atlas';

  track('search', Object.assign({}, { target, category, ...searchParams }));
};

/**
 * CourseList object to manage client side interaction with the course list component
 *
 * @param {DOM element} wrapper
 */
const CourseList = function (wrapper) {
  // return quickly if we don't have an element to bind to
  if (!wrapper) {
    return;
  }

  const courseListFilters = new CourseListFilters(wrapper.querySelector(FILTER_WRAPPER_SELECTOR));
  const courseListPagination = new CourseListPagination(wrapper.querySelector(PAGINATION_SELECTOR));
  const courseListSearch = new CourseListSearch(wrapper.querySelector(SEARCH_SELECTOR));

  /**
   * Parses supported options from the querystring
   */
  const getQueryParams = () => {
    const params = queryString.parse(location.search);
    const result = {};

    for (const key of Object.keys(params)) {
      if (key === 'p') {
        result.page = window.courseListFilterDefaults.page;
        result.page.current = Number(params[key]);
      }
      if (key === 'q') {
        result.query = decodeURIComponent(params[key]);
      }
      ['f_', 'filter_'].forEach((prefix) => {
        if (key.startsWith(prefix)) {
          const filterObj ={ [key.replace(prefix,'')]:  params[key] };
          result['initialFilters'] = Object.assign({}, result['initialFilters'], filterObj);
        }
      });
    }

    return result;
  };

  /**
   * Builds a query string of options and pushes it onto the window history stack
   *
   * @param {integer} page - the page number
   * @param {array}   filters - array of objects  [{filter: '', name: ''}, {filter: '', name: ''}]
   * @param {string}  query - the text value a client is searching for
   */
  const setQueryParams = (page, filters, query = '') => {
    const params = {};
    if ( page && Number(page) > 1 ) {
      params.p = page;
    }
    const usable = filters.filter(input => (input.type === 'checkbox'));
    for (const obj of usable) {
      const key = `f_${obj.filter}`;
      if (!params[key]) {
        params[key] = [];
      }
      params[key].push(obj.name);
    }
    if (query !== '' && query !== window.courseListFilterDefaults['query']) {
      params.q = encodeURIComponent(query);
    }

    //only update query string if it has changed
    const currentSearch = location.search;
    const newSearch = `?${queryString.stringify(params)}`;
    if (currentSearch.replace('?','') !== newSearch.replace('?','')) {
      window.history.pushState(null, document.title, newSearch);
    }
  };

  /**
   * Set search input value
   */
  const setSearchValue = (query) => {
    courseListSearch.setSearchValue(query);
  };

  //configure bare set of options for the client
  const options = { engine: '', display: '', filters: '', query: '' };

  // the course_list mustache file will set the following object based on values set in the yaml
  Object.assign(options, window.courseListFilterDefaults, getQueryParams());

  // target dom elements for template insertion
  const listTarget = wrapper.querySelector(COURSES_SELECTOR);

  const searchClient = new CourseSearch(options.engine);

  /**
   * Fetch facets from the search service to display on the UI
   */
  const fetchFacets = () => {
    const opts = {
      page: { size: 0 },
      facets: options.facets,
      filters: options.filters,
      min_score: options.min_score
    };

    if (opts.facets['start_times']) {
      opts.facets['start_times'] = [courseListFilters.startTimeFacet()];
    }

    if (opts.facets['start_dates']) {
      opts.facets['start_dates'] = [courseListFilters.startDateFacet()];
    }

    searchClient.fetchFacets(opts);
  };

  /**
   * Returns a randomized number in range of a min and max (inclusive)
   *
   * @param {int} min
   * @param {int} max
   */
  const randomIntInRange = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1) + min);
  };

  /**
   * Handles rendering of the results or showing the no results message
   * if there are none.
   *
   * @param {array} results
   * @param {object} page
   */
  const renderResults = (results, page) => {
    wrapper.querySelector('.course-list__loading').classList.add('hidden');
    if (results.length > 0) {
      wrapper.querySelector('.course-list__empty').classList.add('hidden');

      const view = window.courseListFilterDefaults.display;
      listTarget.classList.add(`course-list__courses--${view}-view`);

      // Only create hook object if we want to show the hook in the course list
      if(options.show_hook === true) {
        window.hookInfo = {
          index: (page.current === 1) ? options.hook_index : randomIntInRange(1, results.length-2),
          variant: options.hook.variant
        };
      }

      const itemsHTML = results.map((course, index) => {
        //add the subscriber info to the course used for rendering
        const card = CourseData.render(
          {...course,
          enablements: options.enablements,
          is_client: options.is_client,
          is_student: options.is_student,
          subscriber: options.subscriber,
          students: options.students },
          view
        );

        // If show_hook: true, render hook in the list at the defined index (hook_index)
        // for the first page of results. Render hook at random index on subsequent pages
        if(options.show_hook === true && index === window.hookInfo.index) {
          const hookData = hookUI({...options.hook,
            content: Marked(options.hook.content),
            index: window.hookInfo.index});
          return hookData + card;
        }

        return card;
      }).join(' ');

      listTarget.innerHTML = itemsHTML;
    } else {
      const elem = wrapper.querySelector('.course-list__empty');
      elem.innerHTML = noSearchResults({
        query: options.query,
        filters: courseListFilters.hasFilters()
      });
      elem.classList.remove('hidden');
    }
  };

  /**
   * Builds the filters object to pass to the ElasticSearch client search function
   *
   * @param {object} baseFilters - filters defined in the yaml
   * @param {array} clientFilters - filters extracted from the UI
   */
  const searchFilters = (baseFilters, clientFilters) => {
    const filters = {
      all: [].concat(baseFilters.all || []),
      none: [].concat(baseFilters.none || [])
    };

    if (clientFilters) {
      const { mapped, any } = mappedClientFilters(clientFilters);

      Object.keys(mapped).forEach(key => {
        filters.all.push({ [key]: mapped[key] });
      });

      //if the user is filtering by day of week, we need to exclude the other days
      //to prevent a monday search returning a monday/Wednesday class
      const { days_of_week } = mapped;
      if (days_of_week && mapped.days_of_week.length < 7) {
        const daysWithoutSelected = DAYS_OF_WEEK.filter((day) => !days_of_week.includes(day));
        filters.none.push({ days_of_week: daysWithoutSelected });
      }

      if (any.length) {
        filters.all.push({ any });
      }
    }
    return filters;
  };

  /**
   * Execute the search and render the templated content from the results
   */
  this.search = () => {
    const { page, filters, min_score } = options;
    const opts = {
      page,
      filters: searchFilters(filters, courseListFilters.getSelectedFilters()),
      min_score
    };

    wrapper.querySelector('.course-list__loading').classList.remove('hidden');
    wrapper.querySelector('.course-list__empty').classList.add('hidden');
    listTarget.innerHTML = '';
    courseListPagination.clear();

    if (options.show_filters) {
      setQueryParams(page.current, courseListFilters.getSelectedFilters(), options.query);
    }

    searchClient.search(options.query, opts);
  };

  /**
   * Displays total results for each search query
   */
  const renderTotalResults = ({meta: {page: {total_results}}}) => {
    const resultLabels = document.querySelectorAll('.course-list__result-count');
    for (const label of resultLabels) {
      label.innerText = `${total_results} Results`;
    }
  };

  /**
   * Event handler for the search complete event from the search component
   *
   * @param {object} response from ItemSearch
   */
  const handleSearchComplete = ({ detail: { rawResults, rawInfo } }) => {
    renderResults(rawResults, rawInfo.meta.page);
    courseListPagination.render(rawInfo.meta.page);

    // Setup tracking for course list elements
    [...wrapper.querySelectorAll('[class*="js-track"], [data-track],\
      button, label, a')].forEach(el => {
        el.addEventListener('click', handleEvent);
      });

    const hook = window.hookInfo;
    const query = options.query;
    const { page } = rawInfo.meta;
    const result_uuids = rawResults
      .map(rawResult => rawResult.id);
    const filters = searchFilters(options.filters, courseListFilters.getSelectedFilters());

    renderTotalResults(rawInfo);

    const courseObj = { query, filters, page, result_uuids };
    if(hook) courseObj.hook = hook;
    trackSearch(courseObj);
  };

  /**
   * Event handler for the page event from the pagination control
   *
   * @param {CustomEvent object} destructured to get page
   */
  const handlePage = ({ detail: { page } }) => {
    options.page.current = page;
    scrollTo(listTarget.offsetTop - 100, 100, this.search);
  };

  /**
   * Event handler for browser prev/next navigation
   */
  const handleNavigation = () => {
    const { initialFilters, page, query } = getQueryParams();
    courseListFilters.setSelectedFilters(initialFilters);
    setSearchValue(query);
    options.page.current = (page || 1);
    this.search();
  };

  /**
    * Event handler for text search entry
    */
  const handleSearch = (event) => {
    const { target } = event;
    const value = target.value || (event.detail && event.detail.text);
    try {
      options.query = value.replaceAll('"', '\'');
      options.page.current = 1;
      this.search();
    } catch(error) {
      logError(error, { search: value });
    }
  };

  /**
   * Event handler to process facetsComplete event from the courseSearch class
   *
   * @param {object} custom data event object containing facets data from api search
   * @param {object} initialFilters - filter values that we want to pre-select that
   *   have come in on the querystring.
   */
  const handleFacetsComplete = ({ detail: facets }, initialFilters) => {
    courseListFilters.render({ facets, initialFilters, isVt4sUser: options.is_vt4s_user });
    this.search();
  };

  const handleFilterChange = () => {
    options.page.current = 1;
    this.search();
  };
  /**
   * setupEventHandlers
   */
  const setupEventHandlers = () => {
    document.addEventListener('filter', handleFilterChange);
    document.addEventListener('page', handlePage);
    document.addEventListener('search', handleSearch);
    document.addEventListener('facetsComplete',
      (resultList) => handleFacetsComplete(resultList, options.initialFilters));
    document.addEventListener('searchComplete', handleSearchComplete);
    window.addEventListener('popstate', handleNavigation);
  };

  if (options.show_filters) {
    fetchFacets();
  } else {
    this.search();
  }

  setSearchValue(options.query);
  setupEventHandlers();
};

/**
  * setup.
  */
export function setup() {
  const element = document.querySelector(WRAPPER_SELECTOR);
  window.courseList = new CourseList(element);
}

addStartupScript(setup);
